import { bind, wire } from 'hyperhtml/esm';
import { KatalComponent } from '../../shared/base';

/**
 * @component {kat-checkbox} KatalCheckbox A checkbox is a form element that allows the customer to make a binary choice.
 * @event change This event is fired whenever the checkbox value is changed
 * @status Production
 * @theme flo
 * @example Basic {"label": "Are you sure?", "checked":"false", "constraint-label": ""}
 * @example Constraint {"label": "Are you Sure", "constraint-label":"This means an action will happen", "checked":"true"}
 * @guideline Do Use checkboxes to allow customers to select any number independent of options; including zero, one, or several.
 * @guideline Do Use sentence case with clear concise labels.
 * @guideline Do Use at least label, constriant-label, aria-label, or aria-labelledby to provide a label for screen readers.
 * @guideline The label used for screen readers will use the first available attribute in this order: aria-label, aria-labelledby, label, constraint-label.
 */
class KatalCheckbox extends KatalComponent {
  static get observedAttributes() {
    return [
      'name',
      'value',
      'label',
      'checked',
      'disabled',
      'constraint-label',
      'kat-aria-label',
      'kat-aria-labelledby',
    ];
  }

  constructor() {
    // Manually added indeterminate. Its not actually an observed attribute
    // but needs to be sure an upgrade happens
    super([...KatalCheckbox.observedAttributes, 'indeterminate']);
    this._indeterminate = false;
    this._uniqueLabelId = `kat-checkbox-label-${KatalComponent.generateRandomId()}`;
    this._uniqueConstraintLabelId = `kat-checkbox-constraint-label-${KatalComponent.generateRandomId()}`;
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', event => {
      if (event.target === this) {
        return;
      }
      this.handleClick(event);
    });

    this.addEventListener('keydown', event => {
      if (event.keyCode === 13 || event.keyCode === 32) {
        this.handleClick(event);
      }
    });

    this.render();
  }

  handleClick(event) {
    // we do not want the original event to bubble up,
    // the implementation of the component should be hidden
    event.stopImmediatePropagation();
    if (this.disabled) {
      return;
    }

    const checked = !this.checked;
    this.checked = checked;

    this.dispatchCustomEvent('change', { checked });
  }

  /**
   * @classprop {string} name The name that will be used when sending form data.
   */
  get name() {
    return this.getAttributeOrDefault('name', '');
  }
  set name(value) {
    this.setAttribute('name', value);
  }

  /**
   * @classprop {string} value The value that will be used when sending form data. Defaults to "on".
   */
  get value() {
    return this.getAttributeOrDefault('value', 'on');
  }
  set value(value) {
    this.setAttribute('value', value);
  }

  /**
   * @classprop {string} label This is the text that will be displayed to the user
   */
  get label() {
    return this.getAttribute('label') || '';
  }
  set label(value) {
    this.setAttribute('label', value);
  }

  /**
   * @classprop {string} constraint-label Optional text for the checkbox displayed below the normal label
   */
  get constraintLabel() {
    return this.getAttribute('constraint-label') || '';
  }
  set constraintLabel(value) {
    this.setAttribute('constraint-label', value);
  }

  /**
   * @classprop {string} kat-aria-label Used to label the checkbox when label or constraintLabel doesn't make sense.
   */
  get katAriaLabel() {
    return this.getAttribute('kat-aria-label') || '';
  }
  set katAriaLabel(value) {
    this.setAttribute('kat-aria-label', value);
  }

  /**
   * @classprop {string} kat-aria-labelledby The id of another element used to label the checkbox for accessibility purposes.
   */
  get katAriaLabelledby() {
    return this.getAttribute('kat-aria-labelledby') || '';
  }
  set katAriaLabelledby(value) {
    this.setAttribute('kat-aria-labelledby', value);
  }

  /**
   * @classprop {boolean} checked The state of the checkbox
   */
  get checked() {
    return this.getBooleanAttribute('checked');
  }
  set checked(value) {
    this._indeterminate = false;
    this.setBooleanAttribute('checked', value);
  }

  /**
   * @classprop {boolean} disabled Determines if the checkbox is eligible to be clicked or not
   */
  get disabled() {
    return this.getBooleanAttribute('disabled');
  }
  set disabled(value) {
    this.setBooleanAttribute('disabled', value);
  }

  /**
   * @classprop {boolean} indeterminate Sets the indeterminate state:You can't make a checkbox indeterminate through HTML, only with javascript
   */
  get indeterminate() {
    return this._indeterminate;
  }
  set indeterminate(value) {
    this._indeterminate = value;
    this.render();
  }

  _getClassName() {
    const checkboxClasses = ['checkbox'];

    ['checked', 'disabled', 'indeterminate'].forEach(attr => {
      if (this[attr]) {
        checkboxClasses.push(attr);
      }
    });

    return checkboxClasses.join(' ');
  }

  render() {
    // Accessibility State: https://www.w3.org/TR/wai-aria-1.1/#checkbox

    let ariaLabel = undefined;
    let ariaLabelledBy = undefined;

    if (this.katAriaLabel) {
      ariaLabel = this.katAriaLabel;
    } else if (this.katAriaLabelledby) {
      ariaLabelledBy = this.katAriaLabelledby;
    } else if (this.label) {
      ariaLabelledBy = this._uniqueLabelId;
    }

    let ariaChecked;
    if (this.indeterminate) {
      ariaChecked = 'mixed';
    } else {
      ariaChecked = this.checked ? 'true' : 'false';
    }

    const label = this.label
      ? wire()`
        <kat-label
          id=${this._uniqueLabelId}
          class="kat-checkbox-label-text"
          aria-hidden="true"
          text=${this.label}
        />
      `
      : null;

    const constraintLabel = this.constraintLabel
      ? wire()`
        <kat-label
          id=${this._uniqueConstraintLabelId}
          class="kat-checkbox-constraint-text"
          variant="constraint"
          aria-hidden="true"
          text=${this.constraintLabel}
        />
      `
      : null;

    const hiddenValue =
      this.name && this.checked
        ? wire()`<input type="hidden" name=${this.name} value=${this.value} />`
        : null;

    const tabindex = this.disabled ? null : '0';

    bind(this)`
      <div
        class=${this._getClassName()}
        tabindex=${tabindex}
        role="checkbox"
        aria-checked=${ariaChecked}
        aria-disabled=${this.disabled ? 'true' : 'false'}
        aria-label=${ariaLabel}
        aria-labelledby=${ariaLabelledBy}
        aria-describedby=${
          this.constraintLabel ? this._uniqueConstraintLabelId : undefined
        }
      />
      <div class="text-container">
        ${label}
        ${constraintLabel}
      </div>
      ${hiddenValue}
    `;
  }

  // Respond to attribute changes.
  attributeChangedCallback() {
    this.render();
  }
}

KatalComponent.register('kat-checkbox', KatalCheckbox);
